import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { handleQuerySuccess } from '../../../../hooks/useQuriesSuccess';
import { apiRequest } from '../../../../Services/Common/ApiRequest';
import Global from '../../../../Services/Common/Global';
import useSaveMutation from '../../../../hooks/apis/useSaveMutaion';
import useDeleteMutation from '../../../../hooks/apis/useDeleteMutaion';
import { mapParamsBySchema, mapRowsBySchema } from '../../../../util';
import { COD_BASIC_Schema, stripCodBasicCodeForApi } from './COD_BASIC_Schema';

/** API 배치/단건 응답 → useSaveN용 { isSuccess, message }[] */
export const normalizeCodBasicBatchResults = (res) => {
  let list = [];
  if (Array.isArray(res)) list = res;
  else if (Array.isArray(res?.data)) list = res.data;
  else if (Array.isArray(res?.Data)) list = res.Data;
  else if (res && typeof res === 'object') {
    if ('isSuccess' in res || 'IsSuccess' in res) {
      list = [{ ...res }];
    }
  }
  return list.map((item) => ({
    ...item,
    isSuccess: item.isSuccess ?? item.IsSuccess ?? false,
    message: item.message ?? item.Message ?? item.errorMsg ?? item.ErrorMsg ?? '',
  }));
};

/**
 * GetCodBasicR1 한 줄 — 서버가 `SUBSTRING(tbc.CODE,4) AS Code` 로 내려주므로 Code 는 이미 접두 제거 상태.
 * 그리드 rowId(no) 는 API 필드 우선, 없으면 fallback. stripCodBasicCodeForApi 는 위와 중복이어도 무해.
 */
export const normalizeCodBasicR1Row = (r, rowIndex = 0) => {
  const groupCd = r.groupCd ?? r.GroupCd ?? '';
  const code = stripCodBasicCodeForApi(r.code ?? r.Code ?? '');
  const fallbackNo = `cb:${groupCd}:${code}:${rowIndex}`;
  return {
    no: r.no ?? r.No ?? r.Seq ?? r.seq ?? fallbackNo,
    groupCd,
    code,
    codeNm: r.codeNm ?? r.CodeNm ?? '',
    val1: r.val1 ?? r.Val1 ?? '',
    val2: r.val2 ?? r.Val2 ?? '',
    val3: r.val3 ?? r.Val3 ?? '',
    ref1: r.ref1 ?? r.Ref1 ?? '',
    ref2: r.ref2 ?? r.Ref2 ?? '',
    ref3: r.ref3 ?? r.Ref3 ?? '',
  };
};

/** Utility.GetCodeInfo — MEN_BASIC과 동일 */
export const useSearchCode = (options = {}) => {
  const { onSuccess } = options;

  const query = useQuery({
    queryKey: ['Utility', 'getCodeInfo'],
    queryFn: () => apiRequest('post', Global.urls.Utility.getCodeInfo),
    enabled: true,
  });

  useEffect(() => {
    if (!query.isSuccess) return;

    handleQuerySuccess(query.data, {
      onSuccess: async (rows) => {
        await onSuccess?.(rows);
      },
    });
  }, [query.isSuccess, query.dataUpdatedAt]);

  return query;
};

/** 그룹코드 목록 GetCodBasicR0GroupUp */
export const useSearchGroupUp = (options = {}) => {
  const { onSuccess } = options;

  const query = useQuery({
    queryKey: ['Code', 'getCodBasicR0GroupUp', 'COD_BASIC'],
    queryFn: () =>
      apiRequest('post', Global.urls.Code.getCodBasicR0GroupUp, {}),
    enabled: true,
  });

  useEffect(() => {
    if (!query.isSuccess) return;
    const raw = query.data ?? {};
    if (raw.isSuccess === false || raw.IsSuccess === false) {
      onSuccess?.([]);
      return;
    }
    const arr = Array.isArray(raw.data)
      ? raw.data
      : Array.isArray(raw.Data)
        ? raw.Data
        : [];
    const mapped = arr
      .map((r) => ({
        groupCd: r.groupCd ?? r.GroupCd ?? '',
      }))
      .filter((x) => String(x.groupCd ?? '').trim() !== '');
    onSuccess?.(mapped);
  }, [query.isSuccess, query.dataUpdatedAt]);

  return query;
};

/** 코드 목록 GetCodBasicR1 */
export const useSearch1 = (options = {}) => {
  const { onSuccess } = options;

  const query = useQuery({
    queryKey: ['Code', 'getCodBasicR1', 'COD_BASIC'],
    queryFn: () => {
      const params = mapParamsBySchema(COD_BASIC_Schema.mapper.search1, {
        params: '',
      });
      return apiRequest('post', Global.urls.Code.getCodBasicR1, params);
    },
    enabled: true,
  });

  useEffect(() => {
    if (!query.isSuccess) return;
    const raw = query.data ?? {};
    if (raw.isSuccess === false || raw.IsSuccess === false) {
      handleQuerySuccess({ data: [] }, { onSuccess });
      return;
    }
    const arr = Array.isArray(raw.data)
      ? raw.data
      : Array.isArray(raw.Data)
        ? raw.Data
        : [];
    const mapped = arr.map((row, idx) => normalizeCodBasicR1Row(row, idx));
    handleQuerySuccess({ data: mapped }, { onSuccess });
  }, [query.isSuccess, query.dataUpdatedAt]);

  return query;
};

export const useSave1 = (context = {}, options = {}) => {
  return useSaveMutation(async (rows) => {
    const payload = mapRowsBySchema(rows, COD_BASIC_Schema.mapper.save1, context);
    const res = await apiRequest(
      'post1',
      Global.urls.Code.saveCodBasicU1,
      payload
    );
    return normalizeCodBasicBatchResults(res);
  }, options);
};

export const useDelete1 = (options = {}) => {
  return useDeleteMutation(async (rows) => {
    const payload = mapRowsBySchema(rows, COD_BASIC_Schema.mapper.delete1);
    const res = await apiRequest(
      'post1',
      Global.urls.Code.deleteCodBasicD1,
      payload
    );
    return normalizeCodBasicBatchResults(res);
  }, options);
};
